import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import UpgradeCard from '../../components/UpgradeCard.jsx';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { useAuth } from '../../auth.jsx';
import { Alert, Field } from '../../components/ui.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

// Buyer → seller message (signed-in members, within their plan's daily
// inquiry limit). The server emails it to the seller with Reply-To set to
// the buyer; the seller's address is never shown.
export default function ContactSellerForm({ listingId }) {
  const { t, lang, errorText } = useI18n();
  const { user } = useAuth();
  const blank = { name: user?.name || '', email: user?.email || '', phone: user?.phone || '', quantity: '', message: '', website: '' };
  const [form, setForm] = useState(blank);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const location = useLocation();
  const [left, setLeft] = useState(null); // inquiries left today (null = unlimited/unknown)
  useEffect(() => {
    if (user) api('/membership').then((m) => setLeft(m.inquiries.remaining)).catch(() => {});
  }, [user]);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (form.message.trim().length < 10) return setError(t('errors.message_too_short'));
    if (form.phone && form.phone.replace(/\D/g, '').length !== 10) return setError(t('errors.invalid_phone'));
    setBusy(true);
    try {
      const r = await api(`/marketplace/listings/${listingId}/contact`, { method: 'POST', body: { ...form, lang } });
      setLeft(r.remaining ?? null);
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!user) {
    return (
      <div className="signin-to-contact">
        <p>{t('listing.signInToContact')}</p>
        <Link className="btn btn-primary btn-block" to={`/login?next=${encodeURIComponent(location.pathname)}`}>{t('auth.signinTitle')}</Link>
        <p className="small center">{t('auth.newHere')} <Link to={`/signup?next=${encodeURIComponent(location.pathname)}`}>{t('auth.createAccount')}</Link></p>
      </div>
    );
  }
  if (sent) {
    return <Alert kind="success">{t('listing.contactSent')}{left !== null && ` ${t('listing.inquiriesLeft', { n: left })}`}</Alert>;
  }
  if (left === 0) return user.isSeller ? <Alert>{t('errors.inquiry_limit_reached')}</Alert> : <UpgradeCard reason="inquiries" />;
  return (
    <form className="form" onSubmit={submit}>
      <Field label={t('contact.name')} htmlFor="cs-name">
        <input id="cs-name" value={form.name} onChange={set('name')} autoComplete="name" required maxLength={120} />
      </Field>
      <Field label={t('contact.email')} htmlFor="cs-email">
        <input id="cs-email" type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
      </Field>
      <Field label={t('contact.phone')} optional htmlFor="cs-phone">
        <PhoneInput id="cs-phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
      </Field>
      <Field label={t('listing.quantityWanted')} optional htmlFor="cs-qty">
        <input id="cs-qty" value={form.quantity} onChange={set('quantity')} maxLength={60} placeholder={t('listing.quantityPlaceholder')} />
      </Field>
      <Field label={t('contact.message')} hint={t('contact.messageHint')} htmlFor="cs-message">
        <textarea id="cs-message" rows={5} value={form.message} onChange={set('message')} required maxLength={5000} />
      </Field>
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="cs-website">{t('contact.website')}</label>
        <input id="cs-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} />
      </div>
      <Alert>{error}</Alert>
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>{busy ? t('common.sending') : t('listing.contactSubmit')}</button>
      {left !== null && <p className="field-hint center">{t('listing.inquiriesLeft', { n: left })}</p>}
    </form>
  );
}
